import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, BookOpen, FileCheck, Briefcase, MessageSquare,
  LayoutTemplate, Package, UserCog, ArrowLeft, Sparkles, ShieldCheck, CalendarCheck, Mail,
} from "lucide-react";
import { LogoLockup } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const ITEMS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "adm-dashboard", module: "dashboard" },
  { to: "/admin/students", label: "Students", icon: Users, testId: "adm-students", module: "students" },
  { to: "/admin/courses", label: "Courses", icon: BookOpen, testId: "adm-courses", module: "courses" },
  { to: "/admin/reviews", label: "Reviews", icon: FileCheck, testId: "adm-reviews", module: "reviews" },
  { to: "/admin/jobs", label: "Jobs", icon: Briefcase, testId: "adm-jobs", module: "jobs" },
  { to: "/admin/messages", label: "Messages", icon: MessageSquare, testId: "adm-messages", module: "messages" },
  { to: "/admin/content", label: "Homepage & Content", icon: LayoutTemplate, testId: "adm-content", module: "content" },
  { to: "/admin/community", label: "Community & Homepage", icon: Sparkles, testId: "adm-community", module: "community" },
  { to: "/admin/products", label: "Products & Access", icon: Package, testId: "adm-products", module: "products" },
  { to: "/admin/bookings", label: "Bookings", icon: CalendarCheck, testId: "adm-bookings", module: "bookings" },
  { to: "/admin/marketing", label: "Email Marketing", icon: Mail, testId: "adm-marketing", module: "marketing" },
  { to: "/admin/staff", label: "Staff", icon: UserCog, testId: "adm-staff", module: "staff", adminOnly: true },
  { to: "/admin/owner", label: "Owner Controls", icon: ShieldCheck, testId: "adm-owner", module: "owner", ownerOnly: true },
];

export default function AdminLayout() {
  const nav = useNavigate();
  const { user } = useAuth();
  React.useEffect(() => { document.documentElement.dataset.theme = ""; return () => { document.documentElement.dataset.theme = localStorage.getItem("cr_theme") || ""; }; }, []);

  const items = ITEMS.filter((it) => {
    if (it.ownerOnly) return !!user?.is_owner;
    if (it.adminOnly) return user?.role === "admin";
    const perms = user?.permissions;
    if (perms && typeof perms === "object" && it.module in perms) return perms[it.module] !== false;
    return true;
  });

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-border sticky top-0 h-screen shrink-0">
        <div className="p-5 border-b border-border"><LogoLockup /></div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.testId}
              className={({ isActive }) => cn("flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors",
                isActive ? "bg-teal/10 text-teal" : "text-muted-foreground hover:bg-secondary hover:text-foreground")}>
              <it.icon className="w-5 h-5" /> {it.label}
            </NavLink>
          ))}
        </nav>
        <button onClick={() => nav("/")} className="m-3 flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-secondary" data-testid="back-to-site">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </button>
      </aside>

      <div className="flex-1 min-w-0">
        <div className="lg:hidden bg-white border-b border-border p-3 flex gap-2 overflow-x-auto">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end}
              className={({ isActive }) => cn("flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap",
                isActive ? "bg-teal text-white" : "bg-secondary text-muted-foreground")}>
              <it.icon className="w-4 h-4" /> {it.label}
            </NavLink>
          ))}
        </div>
        <main className="p-4 sm:p-6 max-w-6xl mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}
